import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, Lock, Unlock } from "lucide-react";

const pct = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : "—");
const when = (iso) => new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

const Stat = ({ label, value, sub, testid }) => (
  <div className="rounded-xl border border-[#2a2a2a] bg-[#0a0a0a] px-4 py-3">
    <p className="text-[10px] font-mono uppercase tracking-widest text-[#6b7280]">{label}</p>
    <p data-testid={testid} className="font-heading text-2xl font-semibold mt-1">{value}</p>
    {sub && <p className="text-xs text-[#9ca3af]">{sub}</p>}
  </div>
);

export const EarlyAccessStats = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/early-access").then((r) => setData(r.data)).catch(() => setData({ error: true, drops: [], totals: {} })); }, []);

  if (!data) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#e2ff31]" /></div>;
  const { drops, totals } = data;

  return (
    <div data-testid="early-access-stats" className="bg-[#121212] border border-[#2a2a2a] rounded-2xl p-6 mb-6">
      <h2 className="font-heading text-lg font-semibold">Early access drops</h2>
      <p className="text-sm text-[#9ca3af] mb-5">How past buyers respond to private 24h previews. Opens are approximate, since some mail apps block or pre-load images.</p>
      {data.error ? (
        <p data-testid="early-access-error" className="text-sm text-[#ef4444] py-6 text-center">Couldn't load early-access stats. Refresh to try again.</p>
      ) : drops.length === 0 ? (
        <p data-testid="early-access-empty" className="text-sm text-[#6b7280] py-6 text-center">No early-access drops yet. They start automatically when you upload a beat in a genre that has past buyers.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <Stat testid="ea-total-sent" label="Emails sent" value={totals.sent} sub={`${totals.drops} drop${totals.drops === 1 ? "" : "s"}`} />
            <Stat testid="ea-total-opened" label="Opened" value={totals.opened} sub={`${pct(totals.opened, totals.sent)} open rate`} />
            <Stat testid="ea-total-clicked" label="Clicked" value={totals.clicked} sub={`${pct(totals.clicked, totals.sent)} click rate`} />
            <Stat testid="ea-total-sold-early" label="Sold before public" value={totals.sold_early} sub={`${pct(totals.sold_early, totals.drops)} of drops`} />
          </div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#6b7280] mb-2">“Notify me” signups</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <Stat testid="nm-total-signups" label="Signed up" value={totals.notify_signups} />
            <Stat testid="nm-total-sent" label="“It's live” sent" value={totals.notify_sent} />
            <Stat testid="nm-total-clicked" label="Came back to listen" value={totals.notify_clicked} sub={`${pct(totals.notify_clicked, totals.notify_sent)} click rate`} />
            <Stat testid="nm-total-bought" label="Bought by a signup" value={totals.notify_bought} />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[#6b7280] border-b border-[#2a2a2a]">
                  <th className="py-2 pr-4 font-medium">Beat</th><th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium text-right">Sent</th><th className="py-2 pr-4 font-medium text-right">Opened</th>
                  <th className="py-2 pr-4 font-medium text-right">Clicked</th><th className="py-2 pr-4 font-medium">Pre-public sale</th>
                  <th className="py-2 font-medium text-right whitespace-nowrap">Notify me · sent · back · bought</th>
                </tr>
              </thead>
              <tbody>
                {drops.map((d) => (
                  <tr key={d.id} data-testid={`ea-row-${d.id}`} className="border-b border-[#1e1e1e] last:border-0">
                    <td className="py-3 pr-4 font-medium whitespace-nowrap">{d.title}<span className="block text-xs text-[#6b7280]">{d.genre}</span></td>
                    <td className="py-3 pr-4 whitespace-nowrap">
                      {d.status === "live"
                        ? <span className="inline-flex items-center gap-1 text-xs text-[#e2ff31]"><Lock className="w-3 h-3" />Early · public {when(d.public_at)}</span>
                        : <span className="inline-flex items-center gap-1 text-xs text-[#9ca3af]"><Unlock className="w-3 h-3" />Public since {when(d.public_at)}</span>}
                    </td>
                    <td className="py-3 pr-4 text-right font-mono">{d.sent}</td>
                    <td data-testid={`ea-opened-${d.id}`} className="py-3 pr-4 text-right font-mono">{d.opened} <span className="text-[#6b7280] text-xs">{pct(d.opened, d.sent)}</span></td>
                    <td data-testid={`ea-clicked-${d.id}`} className="py-3 pr-4 text-right font-mono">{d.clicked} <span className="text-[#6b7280] text-xs">{pct(d.clicked, d.sent)}</span></td>
                    <td data-testid={`ea-sold-${d.id}`} className="py-3 pr-4 whitespace-nowrap text-xs">
                      {d.sold_early ? <span className="text-[#22c55e] font-semibold">Sold early · {when(d.sold_at)}</span>
                        : d.sold_at ? <span className="text-[#9ca3af]">Sold after public</span> : <span className="text-[#6b7280]">—</span>}
                    </td>
                    <td data-testid={`nm-row-${d.id}`} className="py-3 text-right font-mono text-xs whitespace-nowrap">
                      {d.notify_signups} · {d.notify_sent} · {d.notify_clicked} · {d.notify_bought ? <span className="text-[#22c55e]">yes</span> : "no"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
